/**
 * detail_map_controller.js — reactive Leaflet detail map (Stimulus, Values API).
 *
 * For the quote and job detail maps, which differ from the static client map
 * (see map_controller.js) in one way: they repaint when the entity's status
 * changes. That repaint is server-driven. On a status change, response_builder
 * emits a Turbo Stream that REPLACES the element holding this controller
 * (target id `<entity>-<id>-map-script`, the suffix 'map-script' in the entity's
 * detail_components). Replacing the element disconnects this controller on the
 * old node — tearing the map down — and connects a fresh one on the new node,
 * which rebuilds the marker with the new status colour. The element-replace IS
 * the reactivity; there is no inline <script> and no client-side polling.
 *
 * Leaflet init/destroy is NOT reimplemented here: connect() reuses
 * window.DetailLocationMap (static/js/utils/detail-location-map.js), the single
 * source of truth for detail-map build/teardown, so the popup markup and the
 * marker stay identical to the rest of the app.
 *
 * Lifecycle. The template paints the server's static map (a Radar Static Maps
 * <img>) inside a button, and that image is the map: the interactive one,
 * about 330 KB of MapLibre and Leaflet, loads only when the button is pressed
 * (activate). Never warm or build it unasked: on a slow link those libraries
 * took the connection for tens of seconds and starved the next page the user
 * opened. In keyless dev there is no image, so connect() builds the map
 * directly, gated on an IntersectionObserver (rootMargin 200px).
 *
 * The build waits on a ResizeObserver for the page CSS that gives the
 * container its height (see _initWhenSized). _init() clears the static image
 * the moment the real map takes over, and teardown restores it so Turbo's
 * cached snapshot shows the image again instead of an empty box.
 * disconnect() and a connect-scoped turbo:before-cache handler both tear down
 * (Turbo clones the snapshot at before-cache time, before disconnect runs, so
 * both are needed).
 *
 * Values (rendered from the model + the status_config Jinja filter; the
 * controller never hardcodes colours):
 *   lat, lng                 coordinates (quote/job site_*_value)
 *   entityId, entityLabel    "Quote 12" / "Job 12" popup heading
 *   instanceKey              registry key DetailLocationMap stores the map under
 *   statusColor, statusTextColor, statusDisplayName   status badge in the popup
 *   clientName, clientAddress                          popup body
 */

import { Controller } from "@hotwired/stimulus";

    /**
     * Lazy-load Leaflet (L), maplibre-gl + its Leaflet bridge (the Radar
     * vector basemap), and the DetailLocationMap helper on demand.
     *
     * On framed detail pages the body swaps into the persistent tab-pane via a
     * Turbo frame; the browser never executes <script> tags that arrive through
     * HTML insertion, so the map's deps can't ride in with the swapped markup.
     * createElement('script') + append DOES execute, so we inject them here the
     * first time a map connects. The promise is cached on window so concurrent
     * connects (status-change stream replaces) and later navigations reuse the
     * single in-flight/settled load — Leaflet is fetched at most once.
     *
     * URLs are passed from the template (data-*-value) so Jinja keeps owning
     * url_for + BUILD_VERSION (asset-path/versioning SSOT).
     */
    function injectScript(src) {
        return new Promise(function (resolve, reject) {
            const s = document.createElement('script');
            s.src = src;
            s.async = false;
            s.onload = resolve;
            s.onerror = () => reject(new Error('Failed to load ' + src));
            document.head.appendChild(s);
        });
    }

    function injectCssOnce(href) {
        if (href && !document.querySelector('link[href^="' + href.split('?')[0] + '"]')) {
            const link = document.createElement('link');
            link.rel = 'stylesheet';
            link.href = href;
            document.head.appendChild(link);
        }
    }

    function mapAssetsReady() {
        return typeof L !== 'undefined' && !!L.maplibreGL && !!window.DetailLocationMap;
    }

    function ensureMapAssets(assets) {
        if (mapAssetsReady()) {
            return Promise.resolve();
        }
        if (window.__detailMapAssetsPromise) {
            return window.__detailMapAssetsPromise;
        }
        injectCssOnce(assets.leafletCss);
        injectCssOnce(assets.maplibreCss);
        // Script order matters: Leaflet defines L, maplibre-gl defines maplibregl,
        // the leaflet-maplibre-gl bridge (L.maplibreGL, the Radar vector basemap
        // path) needs both, and the helper needs all three — so chain them.
        let chain = (typeof L !== 'undefined') ? Promise.resolve() : injectScript(assets.leafletJs);
        if (typeof maplibregl === 'undefined') {
            chain = chain.then(() => injectScript(assets.maplibreJs));
        }
        if (!(typeof L !== 'undefined' && L.maplibreGL)) {
            chain = chain.then(() => injectScript(assets.maplibreLeafletJs));
        }
        if (!window.DetailLocationMap) {
            chain = chain.then(() => injectScript(assets.helperJs));
        }
        // Drop the cache on failure. A transient script-load blip must not
        // leave a permanently rejected promise on window: every later map in
        // the session would reuse it and never build again until a hard
        // reload. Clearing it lets the next connect() retry the injection.
        window.__detailMapAssetsPromise = chain.catch((err) => {
            window.__detailMapAssetsPromise = null;
            throw err;
        });
        return window.__detailMapAssetsPromise;
    }

export default class extends Controller {
        static values = {
            lat: Number,
            lng: Number,
            entityId: Number,
            entityLabel: String,
            instanceKey: String,
            statusColor: String,
            statusTextColor: String,
            statusDisplayName: String,
            clientName: String,
            clientAddress: String,
            leafletJs: String,
            leafletCss: String,
            maplibreJs: String,
            maplibreCss: String,
            maplibreLeafletJs: String,
            helperJs: String,
        };

        connect() {
            // The static <img> placeholder (when present) keeps the card
            // painted until the interactive map replaces it; teardown puts it
            // back for Turbo's snapshot.
            this._staticHtml = this.element.querySelector('.detail-map-static')
                ? this.element.innerHTML
                : null;
            // The static map is on screen and waits for activate().
            if (this._staticHtml) return;
            // Gate the build on visibility: card-based skeleton nav lands every
            // detail section in the DOM immediately, so without this an off-
            // screen map (e.g. a client detail scrolled to Notes) would still
            // eagerly fetch Leaflet + tiles. rootMargin 200px starts the load
            // slightly before the card is on screen so it's ready by the time
            // the user scrolls to it. No-op fallback (load immediately) where
            // IntersectionObserver is unavailable.
            if (!('IntersectionObserver' in window)) {
                this._startLoad();
                return;
            }
            this._observer = new IntersectionObserver((entries) => {
                if (entries.some((entry) => entry.isIntersecting)) {
                    this._observer.disconnect();
                    this._observer = null;
                    this._startLoad();
                }
            }, { rootMargin: '200px' });
            this._observer.observe(this.element);
        }

        /** The static map was pressed: load and build the interactive map. */
        activate() {
            if (this._activated) return;
            this._activated = true;
            this.element.classList.add('detail-map--loading');
            this._startLoad();
        }

        _assets() {
            return {
                leafletJs: this.leafletJsValue,
                leafletCss: this.leafletCssValue,
                maplibreJs: this.maplibreJsValue,
                maplibreCss: this.maplibreCssValue,
                maplibreLeafletJs: this.maplibreLeafletJsValue,
                helperJs: this.helperJsValue,
            };
        }

        _startLoad() {
            // Four scripts back this map: Leaflet (L), maplibre-gl, the
            // leaflet-maplibre-gl bridge (L.maplibreGL — Radar's vector basemap),
            // and the shared build/teardown helper (window.DetailLocationMap,
            // defined by detail-location-map.js via loadOnce). If all are already
            // present (hard load / back-nav / any visit after the first) init
            // immediately.
            if (mapAssetsReady()) {
                this._initWhenSized();
                return;
            }
            // Otherwise lazy-load them on demand. On a framed detail page the body
            // (this element) swaps into the persistent tab-pane via a Turbo frame,
            // and the browser never executes <script> tags inserted as HTML — so
            // the deps can't ride in with the swapped markup and there is no head
            // script to wait on. ensureMapAssets() injects them via
            // createElement('script') (which DOES execute), deduped across maps.
            // Guard _init with isConnected: the controller may disconnect (nav away)
            // while the assets are still loading.
            ensureMapAssets(this._assets())
                .then(() => {
                    if (this.element.isConnected) this._initWhenSized();
                })
                .catch((err) => {
                    // The static map stays, and another press retries the load.
                    this._activated = false;
                    this.element.classList.remove('detail-map--loading');
                    console.error('detail-map: asset load failed', err);
                });
        }

        /**
         * Build only once the container has a real size.
         *
         * The height of these containers (.client-map / .quote-map / .job-map)
         * comes from the page CSS, which detail pages load from a <link> at the
         * FOOT OF THE BODY (entity_detail.html: Turbo merges <head> once but
         * loads body links on every visit, so body placement is what keeps the
         * stylesheet page-scoped). connect() fires as soon as the element is in
         * the DOM — before that stylesheet applies — so the container is often
         * still 0-height here, and IntersectionObserver reports a zero-height
         * box as intersecting.
         *
         * Building then is what produced the intermittent gray map: Leaflet
         * measures the container once, at L.map() time, so a 0x0 measurement
         * leaves the GL canvas 0x0 and puts the marker outside the visible box.
         * The CSS lands a frame or two later, the card becomes 300px tall, and
         * nothing re-measures — only the zoom control and attribution, which
         * CSS pins to the corners, look right.
         *
         * ResizeObserver is the fix rather than another rAF or a timeout: it
         * fires exactly when the stylesheet lands, whatever the network took.
         */
        _initWhenSized() {
            if (this._hasSize() || !('ResizeObserver' in window)) {
                this._init();
                return;
            }
            this._sizeObserver = new ResizeObserver(() => {
                if (!this._hasSize()) return;
                this._sizeObserver.disconnect();
                this._sizeObserver = null;
                this._init();
            });
            this._sizeObserver.observe(this.element);
        }

        _hasSize() {
            return this.element.clientWidth > 0 && this.element.clientHeight > 0;
        }

        _init() {
            if (typeof L === 'undefined' || !window.DetailLocationMap) return;
            // Clear the static placeholder only now, when the interactive map
            // is genuinely about to take over — never earlier, so a failed
            // asset load leaves the image, not an empty gray box.
            if (this._staticHtml) {
                this.element.innerHTML = '';
            }
            this.element.classList.remove('detail-map--loading');
            // L.maplibreGL missing is not fatal: the helper falls back to OSM
            // raster tiles and warns, so a bridge load failure degrades visibly
            // instead of stranding the card blank.

            const map = window.DetailLocationMap.init({
                containerId: this.element.id,
                instanceKey: this.instanceKeyValue,
                entityLabel: this.entityLabelValue,
                entityId: this.entityIdValue,
                lat: this.latValue,
                lng: this.lngValue,
                statusColor: this.statusColorValue,
                statusTextColor: this.statusTextColorValue,
                statusDisplayName: this.statusDisplayNameValue,
                clientName: this.clientNameValue,
                address: this.clientAddressValue,
            });
            if (!map) return;

            // No post-init re-measure here: _initWhenSized() guarantees the
            // container already has a size, so Leaflet's one measurement at
            // L.map() time is correct. Later resizes are Leaflet's own window
            // resize handler.
            this._onBeforeCache = () => this._teardown();
            document.addEventListener('turbo:before-cache', this._onBeforeCache);
        }

        disconnect() {
            if (this._observer) {
                this._observer.disconnect();
                this._observer = null;
            }
            if (this._sizeObserver) {
                this._sizeObserver.disconnect();
                this._sizeObserver = null;
            }
            if (this._onBeforeCache) {
                document.removeEventListener('turbo:before-cache', this._onBeforeCache);
            }
            this._teardown();
        }

        _teardown() {
            if (!window.DetailLocationMap || !this.instanceKeyValue) return;
            const wasLive = !!window.DetailLocationMap.get(this.instanceKeyValue);
            window.DetailLocationMap.destroy(this.instanceKeyValue);
            // Put the static image back after an interactive upgrade, so the
            // Turbo snapshot (before-cache) and any reconnect start from the
            // image again instead of an empty container.
            if (wasLive && this._staticHtml) {
                this.element.innerHTML = this._staticHtml;
            }
        }
    }
